import InstallPromptContext from '@UI/Providers/InstallPromptProvider/InstallPromptContext';
import * as React from 'react';

export type { BeforeInstallPromptEvent } from '@UI/Providers/InstallPromptProvider/InstallPromptContext';

export type InstallPrompt = {
	readonly isAvailable: boolean;
	readonly promptInstall: () => Promise<void>;
};

/**
 * The install prompt is intrinsically web: a native container would have nothing to implement
 * behind it, and `beforeinstallprompt` never fires on iOS Safari at all — `isAvailable` simply stays
 * `false` there, manual installation from the manifest remaining the only path.
 */
function useInstallPrompt(): InstallPrompt {
	const context = React.useContext(InstallPromptContext);

	if (context === null) {
		throw new Error('useInstallPrompt must be used within an InstallPromptProvider.');
	}

	const { deferredEvent, clearDeferredEvent } = context;

	const promptInstall = React.useCallback(async (): Promise<void> => {
		if (!deferredEvent) {
			return;
		}

		await deferredEvent.prompt();
		await deferredEvent.userChoice;
		clearDeferredEvent();
	}, [deferredEvent, clearDeferredEvent]);

	return {
		isAvailable: deferredEvent !== null,
		promptInstall,
	};
}

export default useInstallPrompt;
